import axios from 'axios';
import { defineStore } from 'pinia';
import { API_BASE, api } from '@/api/client';
import { setLanguage } from '@/i18n';
import type { AuthResponse, LegalDocumentDto, MembershipDto, UserDto } from '@/types';

/** Не-httpOnly cookie, которую ставит сервер рядом с refresh-токеном: есть она — есть смысл пробовать refresh. */
const MARKER_COOKIE = 'check_session';

function hasSessionMarker(): boolean {
  try {
    return document.cookie.split(';').some((c) => c.trim().startsWith(`${MARKER_COOKIE}=`));
  } catch {
    return false;
  }
}

/**
 * Сессия браузера. Access-токен только в памяти (не в localStorage): живёт 15 минут
 * и тихо обновляется по httpOnly-cookie. Перезагрузка страницы → bootstrap() → refresh.
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: null as string | null,
    expiresAt: null as string | null,
    user: null as UserDto | null,
    memberships: [] as MembershipDto[],
    /** Непринятые документы: пока список не пуст, поверх приложения висит блокирующий экран. */
    pendingLegal: [] as LegalDocumentDto[],
    /** bootstrap() уже отработал — роутер может решать, пускать ли на страницу. */
    ready: false,
  }),

  getters: {
    isAuthenticated: (s) => s.accessToken !== null && s.user !== null,
    isAdmin: (s) => s.user?.isPlatformAdmin === true,
    /** Пока у человека один клуб; переключение между клубами — не в первой версии. */
    club: (s): MembershipDto | null => s.memberships[0] ?? null,
    clubId(): string | null {
      return this.club?.clubId ?? null;
    },
    isHead(): boolean {
      return this.club?.role === 'head_coach';
    },
    isCoach(): boolean {
      return this.club !== null;
    },
  },

  actions: {
    apply(auth: AuthResponse) {
      this.accessToken = auth.accessToken;
      this.expiresAt = auth.expiresAt;
      this.user = auth.user;
      this.memberships = auth.memberships;
      this.pendingLegal = auth.pendingLegal ?? [];
      if (auth.user.language) setLanguage(auth.user.language);
    },

    setUser(user: UserDto) {
      this.user = user;
      if (user.language) setLanguage(user.language);
    },

    setClub(name: string, timeZone: string) {
      if (this.memberships[0]) {
        this.memberships[0] = { ...this.memberships[0], clubName: name, timeZone };
      }
    },

    clear() {
      this.accessToken = null;
      this.expiresAt = null;
      this.user = null;
      this.memberships = [];
      this.pendingLegal = [];
    },

    /** Сервер ответил 403 legal_acceptance_required — узнаём, что именно нужно принять. */
    async loadPendingLegal() {
      try {
        const { data } = await api.get<LegalDocumentDto[]>('/legal/pending');
        this.pendingLegal = data;
      } catch {
        /* покажем при следующем запросе */
      }
    },

    async acceptLegal(documentIds: string[], language: string) {
      await api.post('/legal/accept', { documentIds, language });
      this.pendingLegal = this.pendingLegal.filter((d) => !documentIds.includes(d.id));
    },

    async bootstrap() {
      if (this.ready) return;
      if (hasSessionMarker()) await this.refresh();
      this.ready = true;
    },

    async login(phone: string, password: string) {
      const { data } = await api.post<AuthResponse>('/auth/login', { phone, password });
      this.apply(data);
    },

    /** Возвращает новый access-токен или null, если сессии больше нет. Без перехватчиков, чтобы не зациклиться. */
    async refresh(): Promise<string | null> {
      try {
        const { data } = await axios.post<AuthResponse>(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
        this.apply(data);
        return data.accessToken;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) this.clear();
        return null;
      }
    },

    async logout() {
      try {
        await api.post('/auth/logout', {});
      } catch {
        /* сервер недоступен — локально всё равно выходим */
      }
      this.clear();
    },

    async logoutAll() {
      try {
        await api.post('/auth/logout-all');
      } catch {
        /* то же */
      }
      this.clear();
    },
  },
});
